import React from 'react';
import { Alert } from '@mui/material';
import type { PermissionSlug } from '../../services/userPermissionsService';

/**
 * The iPhone VERSION NOTE (ADR-0035 A7 as amended, Ali #79 — 2026-09-18).
 * The file keeps its name because cross-repo docs cite it; the API it exports does not.
 *
 * WHAT IT WAS (#66). A `warning` Alert plus a REQUIRED tick — "{First name} does not use
 * the StableTrack iPhone app" — that held Save for any change turning OFF a slug the
 * offline plane reads. The hazard it named is real and unchanged: on an iOS build below
 * the version below, a per-op `PERMISSION_DENIED` lands in SyncEngine's `default:` arm,
 * which discards the server's message and burns a retry; at maxRetries = 3 the op stops
 * being revived, drops out of the unsynced count, and a re-grant never resets the
 * counter — so the work is retained on the device where nobody can see or re-send it.
 *
 * WHAT IT IS NOW (#79). The release ORDER is reversed: iOS 1.3.0 ships FIRST and the
 * editor merges only once it is "Ready for Sale". 1.3.0 parks a refused op under
 * More → Needs attention with Retry/Discard and re-pulls the window when permissions
 * change, so the remedy exists before the screen that needs it — and the instruction
 * becomes one a clinic can act on: update the app. The block is ONE informational line,
 * there is no tick, and Save is never blocked.
 *
 * WHY A NOTE AND NOT A GATE. The web cannot know which build anyone runs — the app
 * sends no client header and reports no version to the server, `User` has no platform
 * column and there is no session table — and the app has no force-update mechanism, so
 * nothing here can make a phone current. A control that asks a question the screen
 * cannot verify, to hold a save the server will accept, is a control reporting a change
 * it did not make (INV-106). The line addresses the CLINIC, which is the only party
 * that knows, and states the residual risk instead of pretending to close it: a phone
 * still below the floor keeps losing offline work silently until somebody updates it.
 *
 * Every clause is scoped to what the two shipped builds do. Below the floor the refusal
 * is PER OPERATION (`sync.service.ts:1543-1557`), not "anything they save", and it is
 * silent — no screen surfaces the stranded op. "Stop syncing without any notice" is
 * therefore the whole claim, and it promises nothing about a build that does not exist.
 *
 * The WEB PWA needs no equivalent and that asymmetry is deliberate: `pushQueue.ts`
 * stores the server's sentence verbatim, `ConflictReviewDialog` shows it with
 * Retry/Discard, and Retry resets the budget.
 *
 * ⚠ If the iOS minimum ever changes, change `IOS_MIN_SAFE_VERSION` only — both sentences
 * are built from it. The KB article (`backend/scripts/kb-seed-data.ts`) holds a COPY of
 * the generic sentence because a backend script cannot import from the web app; the
 * weld is `backend/src/auth/permissions-docs.spec.ts`, which pins both spellings and
 * this constant, so a re-word or a bump in one file alone fails the build.
 */
export const IOS_MIN_SAFE_VERSION = '1.3.0';

/**
 * The second half, shared by both forms and held as ONE literal rather than wrapped
 * across lines: the docs gate greps this file for the sentence it pins in the KB copy,
 * and a clause split over two source lines is a clause that weld cannot see.
 */
const OLDER_VERSIONS_CLAUSE =
  'On older versions, offline changes that need an ability you turn off stop syncing without any notice.';

/**
 * The line shown beside Save and on the role-change confirm. Exported so both renderers
 * assert the SAME words — neither retypes them, so one clinic can never be shown two
 * spellings of one instruction.
 */
export const iosVersionNote = (firstName: string): string =>
  `If ${firstName} uses the StableTrack iPhone app, ask them to update it to version ${IOS_MIN_SAFE_VERSION} or later first. ${OLDER_VERSIONS_CLAUSE}`;

/**
 * The same sentence where there is no name to use — the assistant's KB article, which a
 * clinic reads outside any dialog. Same claims, same version, same source of truth.
 */
export const IOS_VERSION_NOTE_GENERIC = `If that person uses the StableTrack iPhone app, ask them to update it to version ${IOS_MIN_SAFE_VERSION} or later first. ${OLDER_VERSIONS_CLAUSE}`;

/**
 * Rendered in exactly the places the gate appeared and under exactly the same condition:
 * a save, a Reset or a role change that would turn OFF a slug in `affectsOfflineSync`.
 * Grants, non-sync narrowing and a Reset that only widens show nothing — which is why
 * this takes the LIST rather than a boolean.
 *
 * `alwaysShow` is for the one caller that cannot measure: on the role-change confirm an
 * empty list means "the actor may not read the target's permissions", not "nothing
 * narrows", so that arm opts in explicitly rather than passing a fabricated list.
 */
export function IosVersionNote(props: {
  firstName: string;
  syncNarrowed: readonly PermissionSlug[];
  alwaysShow?: boolean;
}): JSX.Element | null {
  const { firstName, syncNarrowed, alwaysShow } = props;
  if (!alwaysShow && syncNarrowed.length === 0) return null;

  return (
    <Alert severity="info" sx={{ mt: 1, mb: 1 }}>
      {iosVersionNote(firstName)}
    </Alert>
  );
}
